import * as React from 'react';
import { EntityPicker } from '@/components/custom/entity-picker';
import type { EntityPickerConfig } from '@/components/custom/entity-pickers';
import type { LoadStrategy } from '@/components/custom/use-entity-autocomplete';

interface EntityMultiSelectProps<T> extends EntityPickerConfig<T> {
  /** Currently-selected ids. Empty = "no filter" → the trigger shows `allLabel`. */
  selected: readonly string[];
  onChange: (next: string[]) => void;

  loadStrategy?: LoadStrategy;
  /** Per-page size for 'lazy'/'always' fetches — see `EntityPicker`. Call
   *  sites spread a picker config in, so this normally arrives with it (500
   *  for customers and employees) and needs no thought; pass it explicitly
   *  only to override one filter. Capped at the hook's server-limit ceiling. */
  lazyLimit?: number;
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  /** Placeholder for the in-popover search input. */
  searchPlaceholder?: string;
  /** Trigger label when nothing is selected. */
  allLabel?: string;
  /** Popover sizing — see `EntityPicker`. 'auto' lets rows with detail
   *  (e.g. an employee email line) widen the dropdown to fit. */
  popoverWidth?: 'anchor' | 'auto';
  className?: string;
  id?: string;
}

/**
 * Backwards-compatible adapter: the multi-select customer/employee/department
 * filters are now a thin shim over the unified `EntityPicker` in multi mode.
 * The flat-closures API is preserved so existing call sites (timesheet report,
 * approvals) don't change. New code should prefer `EntityPicker` directly
 * with an `EntityPickerConfig`.
 *
 * The config is collected by REST, never re-listed field by field. Call sites
 * spread a whole `EntityPickerConfig` in, and this used to name the handful of
 * members it forwarded — so anything the config grew was silently dropped on
 * the floor, with JSX spread suppressing the excess-property error that would
 * otherwise have caught it. `searchText` and `maxLimit` were both lost that
 * way: the timesheet report's Department filter runs in `preload` mode, where
 * `searchText` IS the in-memory filter, so a department was findable by name
 * and not by description. Whatever the config gains next now forwards itself.
 */
export function EntityMultiSelect<T>({
  selected,
  onChange,
  loadStrategy = 'lazy',
  lazyLimit,
  renderItem,
  searchPlaceholder = 'Search…',
  allLabel = 'All',
  popoverWidth,
  className,
  id,
  ...config
}: EntityMultiSelectProps<T>) {
  return (
    <EntityPicker<T>
      multiple
      config={config}
      value={selected}
      onChange={(ids) => onChange(ids)}
      loadStrategy={loadStrategy}
      lazyLimit={lazyLimit}
      renderItem={renderItem}
      searchPlaceholder={searchPlaceholder}
      placeholder={allLabel}
      popoverWidth={popoverWidth}
      className={className}
      id={id}
    />
  );
}
